import React, { useCallback, useState } from 'react';
import {
  Box, FormControlLabel, Stack, Switch, Typography,
} from '@mui/material';

import useComponentMounted from '../../../../hooks/useComponentMounted';
import useSessionStore from '../../../../hooks/useSessionStore';
import useToast from '../../../hooks/useToast';

import LeadReengagementSequence from '../../../../Model/LeadReengagementSequence';
import { LoadingButton } from '../../../../components/Button';
import { reportError } from '../utils/reportError';
import { fromStoredImage, toStoredImage, uploadCampaignImage } from '../utils/uploadCampaignImage';
import { submitErrorMessage } from '../utils/errorMessages';
import { SUBMIT_PHASE } from '../utils/constants';

import CoachSection from '../components/CoachSection';
import SectionCard from '../components/SectionCard';
import LoadingRow from '../components/LoadingRow';
import { useDplOptions } from '../hooks/useDplOptions';
import texts from '../texts.json';
import { GridLayout } from '../styles';

import { useReEngagementSequence } from './hooks/useReEngagementSequence';
import EligibilitySection from './sections/EligibilitySection';
import ScheduledTextsSection from './sections/ScheduledTextsSection';
import ReEngagementPreview from './SidePanel/ReEngagementPreview';

const copy = texts.reengagement;

const ReEngagementTab = () => {
  const {
    coach,
    setCoach,
    sequenceId,
    setSequenceId,
    active,
    setActive,
    eligibility,
    toggleSalesStage,
    toggleCountry,
    setRequireConsent,
    blasts,
    addBlast,
    removeBlast,
    updateBlastBody,
    updateBlastOffset,
    updateBlastDpl,
    updateBlastImage,
    loading,
    derived,
  } = useReEngagementSequence();

  const { showToast } = useToast();
  const { userId } = useSessionStore();
  const isMountedRef = useComponentMounted();
  const [savePhase, setSavePhase] = useState(SUBMIT_PHASE.IDLE);
  const [imageErrors, setImageErrors] = useState({});
  const isSubmitting = savePhase !== SUBMIT_PHASE.IDLE;

  const handleImageError = useCallback((key, hasError) => {
    setImageErrors((prev) => (!!prev[key] === hasError ? prev : { ...prev, [key]: hasError }));
  }, []);

  const coachId = coach ? coach.id : null;
  const { options: dplOptions, status: dplStatus } = useDplOptions(coachId);
  const { coachName } = derived;
  // Keyed by blast, so errors on removed blasts (or a previous coach's) stop counting on their own.
  const hasImageError = blasts.some((b) => imageErrors[b.key]);
  const isReady = derived.isValid && !hasImageError;
  const saveCta = active ? copy.save.cta : copy.save.ctaInactive;
  const savingLabel = savePhase === SUBMIT_PHASE.UPLOADING ? copy.save.uploadingImage : copy.save.saving;
  const saveLabel = isSubmitting ? savingLabel : saveCta;

  const handleSave = useCallback(async () => {
    if (!isReady || isSubmitting) return;
    const willUpload = blasts.some((b) => !!b.image?.file);
    setSavePhase(willUpload ? SUBMIT_PHASE.UPLOADING : SUBMIT_PHASE.SAVING);
    try {
      const uploaded = await Promise.all(
        blasts.map((b) => (b.image?.file ? uploadCampaignImage({ file: b.image.file }) : null)),
      );
      if (!isMountedRef.current) return;
      if (willUpload) setSavePhase(SUBMIT_PHASE.SAVING);

      const blastsPayload = blasts.map((b, idx) => {
        const storedImage = uploaded[idx] || (b.image?.url ? toStoredImage(b.image) : null);
        return {
          blastId: b.blastId,
          dayOffset: b.dayOffset,
          bodyTemplate: b.body,
          dpl: b.dpl.url,
          ...(storedImage ? { image: storedImage } : {}),
        };
      });

      const savedId = await LeadReengagementSequence.upsert({
        id: sequenceId,
        coachId,
        active,
        eligibility,
        blasts: blastsPayload,
        userId: userId || null,
      });
      if (!isMountedRef.current) return;
      setSequenceId(savedId);
      // Swap the local File for its stored form so a later save does not re-upload it.
      blasts.forEach((b, idx) => {
        if (uploaded[idx]) updateBlastImage(b.key, fromStoredImage(uploaded[idx]));
      });
      setSavePhase(SUBMIT_PHASE.IDLE);
      showToast(copy.saveStatus.saved);
    } catch (err) {
      reportError('saveReengagementSequence', err);
      if (!isMountedRef.current) return;
      setSavePhase(SUBMIT_PHASE.IDLE);
      showToast(submitErrorMessage(err, copy.saveStatus.error), { error: true });
    }
  }, [
    isReady, isSubmitting, blasts, sequenceId, setSequenceId, updateBlastImage,
    coachId, active, eligibility, userId, showToast, isMountedRef,
  ]);

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h6" fontWeight={700}>{copy.title}</Typography>
        <Typography variant="body2" color="text.secondary">{copy.subtitle}</Typography>
      </Box>

      <GridLayout>
        <Stack spacing={2} sx={{ minWidth: 0 }}>
          <CoachSection onChange={setCoach} />

          <SectionCard>
            <FormControlLabel
              control={<Switch checked={active} onChange={(e) => setActive(e.target.checked)} />}
              label={copy.enableToggle}
            />
          </SectionCard>

          {loading ? (
            <SectionCard>
              <LoadingRow label={copy.loading} />
            </SectionCard>
          ) : (
            <>
              <EligibilitySection
                eligibility={eligibility}
                onToggleSalesStage={toggleSalesStage}
                onToggleCountry={toggleCountry}
                onRequireConsentChange={setRequireConsent}
              />
              <ScheduledTextsSection
                blasts={blasts}
                blastsMeta={derived.blastsMeta}
                dplOptions={dplOptions}
                dplStatus={dplStatus}
                onBodyChange={updateBlastBody}
                onOffsetChange={updateBlastOffset}
                onDplChange={updateBlastDpl}
                onImageChange={updateBlastImage}
                onImageError={handleImageError}
                onRemoveBlast={removeBlast}
                onAddBlast={addBlast}
                coachName={coachName}
              />
            </>
          )}
        </Stack>

        <Stack spacing={2} sx={{ position: 'sticky', top: 24, minWidth: 0 }}>
          <ReEngagementPreview blasts={blasts} coachName={coachName} />
          <LoadingButton
            full
            isLoading={isSubmitting}
            disabled={!isReady}
            onClick={handleSave}
          >
            {saveLabel}
          </LoadingButton>
        </Stack>
      </GridLayout>
    </Box>
  );
};

export default ReEngagementTab;
